import { useMemo, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton } from "../components/States";
import { listCrew } from "../lib/data";
import { crewSignInLink, giveCrewLogin, saveCrewMember, type CrewFields } from "../lib/jobs-data";
import { BRAND } from "../lib/config";
import { missingTickets, recencyLapsed, rosteredCrew, TICKETS, ticketShort } from "../lib/tickets";
import type { CrewMember } from "../lib/supabase";
import { useAsync } from "../lib/useAsync";
import { InsuranceBlock, insuranceChip, InsurancePanel, insuranceInUse } from "../modules/crew-records/Records";

const REQUIRED_LABEL = { everyone: "everyone", leadingHand: "leading hands", none: "" } as const;
const monthLabel = (ymd: string) => new Date(`${ymd}T00:00:00Z`).toLocaleDateString("en-AU", { month: "short", year: "numeric", timeZone: "UTC" });

function Held({ held }: { held: boolean }) {
  return held ? <Chip tone="green">Held</Chip> : <span className="muted">No</span>;
}

export function Crew() {
  const { data: all, error, loading, reload } = useAsync(async () => {
    const [crew, rostered] = await Promise.all([listCrew(), rosteredCrew()]);
    return { crew, missing: new Map(missingTickets(crew, rostered).map((m) => [m.crewId, m])) };
  });
  const data = all?.crew;
  const needsAttention = (m: CrewMember) => m.active && (all?.missing.has(m.id) || recencyLapsed(m));
  const [q, setQ] = useState("");
  const [show, setShow] = useState("active");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((m) => {
      if (show === "active" && !m.active) return false;
      if (show === "attention" && !needsAttention(m)) return false;
      if (show === "inactive" && m.active) return false;
      return !needle || m.full_name.toLowerCase().includes(needle) || (m.phone ?? "").replace(/\s/g, "").includes(needle.replace(/\s/g, ""));
    });
  }, [all, q, show]);
  const attention = (data ?? []).filter(needsAttention).length;
  const [editing, setEditing] = useState<CrewMember | "new" | null>(null);
  const [insuring, setInsuring] = useState<CrewMember | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { notice, setNotice, fail, ok } = useNotice();

  async function login(m: CrewMember) {
    setBusy(m.id);
    setNotice(null);
    try {
      const r = await giveCrewLogin(m.id);
      ok(`${m.full_name} can now sign in. ${r.emailed ? `A sign-in link was emailed to ${m.email}.` : ""} If it doesn't arrive, use Copy sign-in link and text it.`);
      reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  async function copyLink(m: CrewMember) {
    setBusy(m.id);
    setNotice(null);
    try {
      const url = await crewSignInLink(m.id);
      await navigator.clipboard.writeText(url);
      ok(`Sign-in link for ${m.full_name} copied. Text it to them: it works once and expires in about an hour.`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Operations"
        title="Crew & tickets"
        meta={TICKETS.length
          ? `Tickets held: ${TICKETS.map((t) => `${ticketShort(t)} = ${t.label}${t.requiredFor !== "none" ? ` (needed by ${REQUIRED_LABEL[t.requiredFor]})` : ""}`).join("; ")}. Red = rostered in the next 14 days without a ticket they need.${BRAND.field?.recencyMonths ? ` Last worked or trained is flagged after ${BRAND.field.recencyMonths} months.` : ""} Licence and ticket numbers are not stored.`
          : "Licence and ticket numbers are not stored."}
        actions={<button type="button" className="btn btn--primary" onClick={() => setEditing("new")}><Icon name="plus" size={18} /> Add crew member</button>}
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <InsuranceBlock />
      {insuring && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">{insuring.full_name}: insurance</h2><button type="button" className="btn btn--outline btn--xs" onClick={() => setInsuring(null)}>Close</button></div>
          <div className="card__body"><InsurancePanel crewId={insuring.id} onChanged={reload} /></div>
        </div>
      )}
      {editing && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">{editing === "new" ? "Add crew member" : `Edit ${editing.full_name}`}</h2></div>
          <div className="card__body">
            <CrewForm member={editing === "new" ? null : editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); ok("Crew details saved."); reload(); }} />
          </div>
        </div>
      )}

      <FilterBar count={data ? `Showing ${plural(rows.length, "person", "people")}` : undefined}>
        <SearchField label="Search crew" placeholder="Search name or phone" value={q} onChange={setQ} />
        <SelectField label="Show" value={show} onChange={setShow} options={[
          { value: "active", label: "Active crew" },
          { value: "attention", label: `Needs attention${data ? ` (${attention})` : ""}` },
          { value: "inactive", label: "Inactive" },
          { value: "all", label: "Everyone" },
        ]} />
      </FilterBar>

      {loading && !data && <Skeleton columns={6 + TICKETS.length} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState help icon="hardhat" title="No crew members yet">{BRAND.field ? "Add each person with their mobile, email and the tickets they hold." : "Add each traffic controller with their mobile, email and ticket dates."}</EmptyState>}
      {data && data.length > 0 && rows.length === 0 && <EmptyState icon="search" title="No matching crew">Try a different search or filter.</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead>
              <tr>
                <th scope="col">Name</th><th scope="col">Phone</th>
                {TICKETS.map((t) => <th key={t.id} scope="col">{t.short ? <abbr title={t.label}>{t.short}</abbr> : t.label}</th>)}
                <th scope="col">Licence</th><th scope="col">Last worked</th><th scope="col">Status</th><th scope="col">Login</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const missing = all?.missing.get(m.id);
                const lapsed = recencyLapsed(m);
                return (
                  <tr key={m.id} className={m.active ? undefined : "is-muted"}>
                    <td data-label="Name" className="strong">
                      {m.full_name}
                      {missing && <span className="sub sub--primary">Rostered without {missing.missing.map(ticketShort).join(", ")}</span>}
                    </td>
                    <td data-label="Phone" className="nowrap">{m.phone ? <a className="tel" href={`tel:${m.phone.replace(/\s/g, "")}`}>{m.phone}</a> : <span className="muted">None</span>}</td>
                    {TICKETS.map((t) => (
                      <td key={t.id} data-label={ticketShort(t)}>
                        {missing?.missing.some((x) => x.id === t.id) ? <Chip tone="primary">Missing</Chip> : <Held held={(m.tickets_held ?? []).includes(t.id)} />}
                      </td>
                    ))}
                    <td data-label="Licence">{m.driver_licence_class ?? <span className="muted">Not set</span>}</td>
                    <td data-label="Last worked">
                      {m.last_worked_month ? (lapsed ? <Chip tone="primary">{monthLabel(m.last_worked_month)}</Chip> : monthLabel(m.last_worked_month)) : <span className="muted">Not set</span>}
                    </td>
                    <td data-label="Status"><Chip value={m.active ? "active" : "inactive"} />{insuranceInUse() && <> {insuranceChip(m.public_liability_expiry ?? null, Boolean(m.is_partner))}</>}</td>
                    <td className="actions actions--stack">
                      <button type="button" className="btn btn--outline btn--xs" onClick={() => setEditing(m)}>Edit</button>
                      {insuranceInUse() && !m.is_partner && <button type="button" className="btn btn--outline btn--xs" onClick={() => setInsuring(m)}>Insurance</button>}
                      {m.active && !m.profile_id && (
                        m.email
                          ? <button type="button" className="btn btn--ink btn--xs" disabled={busy === m.id} onClick={() => void login(m)}>Give login</button>
                          : <span className="small muted">Add email for a login</span>
                      )}
                      {m.profile_id && <button type="button" className="btn btn--outline btn--xs" disabled={busy === m.id} onClick={() => void copyLink(m)}>Sign-in link</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const blankToNull = (s: string) => s.trim() || null;

function CrewForm({ member, onSaved, onCancel }: { member: CrewMember | null; onSaved: () => void; onCancel: () => void }) {
  const [f, setF] = useState({
    full_name: member?.full_name ?? "",
    phone: member?.phone ?? "",
    email: member?.email ?? "",
    tickets_held: member?.tickets_held ?? [],
    driver_licence_class: member?.driver_licence_class ?? "",
    last_worked_month: member?.last_worked_month?.slice(0, 7) ?? "",
    notes: member?.notes ?? "",
    active: member?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const set = (k: "full_name" | "phone" | "email" | "driver_licence_class" | "last_worked_month" | "notes") => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggle = (id: string, on: boolean) => setF((x) => ({ ...x, tickets_held: on ? [...new Set([...x.tickets_held, id])] : x.tickets_held.filter((t) => t !== id) }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!f.full_name.trim()) return setNotice({ tone: "error", text: "Enter a name." });
    setBusy(true);
    try {
      const fields: CrewFields = {
        full_name: f.full_name.trim(), phone: blankToNull(f.phone), email: blankToNull(f.email), active: f.active, notes: blankToNull(f.notes),
        // Keep ids no longer in the client layer's list, so nothing recorded is lost.
        tickets_held: f.tickets_held,
        driver_licence_class: blankToNull(f.driver_licence_class),
        last_worked_month: f.last_worked_month ? `${f.last_worked_month}-01` : null,
      };
      await saveCrewMember(member?.id ?? null, fields);
      onSaved();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  const field = (label: string, input: React.ReactNode, hint?: string, wide?: boolean) => (
    <label className={`field${wide ? " field--wide" : ""}`}><span className="field__label">{label}</span>{input}{hint && <span className="field__hint">{hint}</span>}</label>
  );

  return (
    <form className="form" onSubmit={submit}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="form-grid">
        {field("Full name", <input className="input" required value={f.full_name} onChange={set("full_name")} autoComplete="off" />)}
        {field("Mobile", <input className="input" type="tel" value={f.phone} onChange={set("phone")} />)}
        {field("Email", <input className="input" type="email" value={f.email} onChange={set("email")} />, "Needed for a login. Use their own email.")}
        {field("Driver licence class", <input className="input" value={f.driver_licence_class} onChange={set("driver_licence_class")} placeholder="e.g. C or C (P2)" />)}
        {field("Last worked or trained", <input className="input" type="month" value={f.last_worked_month} onChange={set("last_worked_month")} />, BRAND.field?.recencyMonths ? `Flagged after ${BRAND.field.recencyMonths} months` : undefined)}
        {field("Notes", <textarea className="input" rows={2} value={f.notes} onChange={set("notes")} />, undefined, true)}
      </div>
      {TICKETS.length > 0 && (
        <fieldset className="checks">
          <legend className="field__label">Tickets held</legend>
          {TICKETS.map((t) => (
            <label key={t.id} className="check">
              <input type="checkbox" checked={f.tickets_held.includes(t.id)} onChange={(e) => toggle(t.id, e.target.checked)} /> {t.label}{t.short && t.short !== t.label ? ` (${t.short})` : ""}
            </label>
          ))}
        </fieldset>
      )}
      <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => setF((x) => ({ ...x, active: e.target.checked }))} /> Active (can be put on shifts)</label>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Saving" : "Save"}</button>
        <button type="button" className="btn btn--outline" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}
